import { NavIcon } from '@/components/icons/NavIcon';
import styles from './Pagination.module.css';

interface PaginationProps {
  page: number;
  total: number;
  pageSize: number;
  onChange: (next: number) => void;
  // 현재 페이지 양옆에 보일 페이지 수(기본 1). 2면 최대 5개 연속 번호가 보인다.
  siblingCount?: number;
  // 정렬 — 'between'(기본, meta 좌 + 컨트롤 우) / 'center'(컨트롤 가운데).
  align?: 'between' | 'center';
  // 버튼 크기 — 'md'(기본) / 'lg'.
  size?: 'md' | 'lg';
}

export function Pagination({
  page,
  total,
  pageSize,
  onChange,
  siblingCount = 1,
  align = 'between',
  size = 'md',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // 현재 페이지 주변 window를 고정 크기로 유지 — 가장자리(1페이지/마지막)에서도 개수 보장.
  const windowSize = siblingCount * 2 + 1;
  let winStart = Math.max(1, page - siblingCount);
  let winEnd = Math.min(totalPages, page + siblingCount);
  if (winEnd - winStart + 1 < windowSize) {
    if (winStart === 1) winEnd = Math.min(totalPages, winStart + windowSize - 1);
    else if (winEnd === totalPages) winStart = Math.max(1, winEnd - windowSize + 1);
  }

  // 1 … [window] … last
  const pages: (number | '...')[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= winStart && p <= winEnd)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  const wrapClass = `${styles.wrap} ${align === 'center' ? styles.center : ''} ${size === 'lg' ? styles.lg : ''}`;

  return (
    <div className={wrapClass}>
      <div className={styles.meta}>
        <span className={styles.strong}>
          {start}–{end}
        </span>
        <span> / 전체 {total}개</span>
      </div>
      <div className={styles.controls}>
        <button
          type="button"
          className={`${styles.btn} ${styles.iconBtn}`}
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          aria-label="이전 페이지"
        >
          <NavIcon name="chev-l" size={size === 'lg' ? 13 : 11} />
        </button>
        {pages.map((v, i) =>
          v === '...' ? (
            <span key={`e${i}`} className={styles.ellipsis}>
              …
            </span>
          ) : (
            <button
              key={v}
              type="button"
              className={`${styles.btn} ${v === page ? styles.active : ''}`}
              onClick={() => onChange(v)}
            >
              {v}
            </button>
          ),
        )}
        <button
          type="button"
          className={`${styles.btn} ${styles.iconBtn}`}
          disabled={page === totalPages}
          onClick={() => onChange(page + 1)}
          aria-label="다음 페이지"
        >
          <NavIcon name="chev-r" size={size === 'lg' ? 13 : 11} />
        </button>
      </div>
    </div>
  );
}
